import { type StateCreator } from 'zustand';
import {
  beginAudioShutdown,
  drainAudioWork,
  getPendingAudioWorkCount,
  getPendingAudioWorkKinds,
  resumeAudioWork,
  waitForSettledAudioWork,
} from '../audioWorkBarrier';
import { captureFlags } from '../configure/flags-facade';
import { capturePerf } from '../configure/perf-facade';
import { captureUserContext } from '../configure/user-context-facade';
import { db } from '../db';
import {
  getSafeErrorFields,
  getUnwrappedErrorName,
  isStorageDegradation,
  isStoreUnusableForConsult,
} from '../errors';
import {
  armMemoryOnlyWrites,
  armUnusableStoreWrites,
  createRecording as createRecordingV2,
  disarmUnusableStoreWrites,
  getPipelineMode,
  hasDrainableRetainedChunks,
  isMemoryOnlyWriteError,
  isWriteFenceStaleError,
  peekRecordingRow,
  prepareWriteFenceForHydrate,
  requestPersistentStorage,
  resolvePipelineMode,
  runCanary,
  setLastChunkIndex,
  setRecordingEndDate as setRecordingEndDateV2,
} from '../persistenceV2';
import type { RecordingKey } from '../persistenceV2/types';
import {
  AudioStorageRecoveryFailed,
  CreateRecordingIdFailed,
  DexieStartError,
  RecordingActiveInAnotherTabError,
  type SessionRecordingConfig,
  type SessionRecordingStore,
  StartRecordingCancelled,
  StartRecordingFailed,
  type Status,
  TooLowStorageError,
} from '../types';
import { useAudioGraphStore } from '../useAudioGraphStore';
import { checkStorageAvailable, getErrorInfo, sleep } from '../utils';
import { deleteFinishedRecordings } from './lib/deleteFinishedRecordings';
import { hashDeviceLabel } from './lib/hashDeviceLabel';
import { logger } from './lib/logger';
import { tryToGetRecordingId } from './lib/tryToGetRecordingId';
import {
  discardBackupCaptureSlotV2,
  stopBackupCaptureV2,
} from './v2/backupCaptureLifecycleV2';
import { discardCaptureSlotV2, stopCaptureV2 } from './v2/captureLifecycleV2';
import {
  adoptCaptureLock,
  type CaptureLockToken,
  hasAnyLocalCaptureLease,
  hasLocalCaptureLease,
  markAdoptableTail,
  releaseCaptureLock,
  tryAcquireCaptureLock,
} from './v2/recordingLockV2';
import { cleanupRecordingsV2 } from './v2/recordingV2';
import { isRetryDrainActive, pokeRetryDrain } from './v2/retryDrainV2';
import {
  reportStorageDegradedOnce,
  resetStorageDegradedReport,
} from './v2/storageDegradedReport';

// v2-dispatch wrappers: start()/stop()/handleStopError() route storage calls
// through the pinned pipeline mode instead of the legacy db directly.
const persistCreateRecording = (
  sessionId: string,
  recordingId: string,
  useTranscriptionServiceV2: boolean,
  userRefId: string
) =>
  getPipelineMode() === 'ledger'
    ? createRecordingV2({
        sessionId,
        recordingId,
        userRefId,
        useTranscriptionServiceV2,
      })
    : db.createRecording(sessionId, recordingId, useTranscriptionServiceV2, userRefId);

/** One `LEDGER_STARTED_WITHOUT_STORAGE` payload per start. */
type StorageLessReport = (details: Record<string, unknown>) => void;

/** Stages, then raises, the one `onStorageDegraded` call this recording gets. */
type StorageDegradedReport = (error: unknown) => void;

// One error type for every step, so the port's payload stays one shape.
const storageDegradedError = (step: 'fence' | 'create' | 'canary'): TooLowStorageError =>
  new TooLowStorageError(`audio storage unusable (${step}); recording from memory`);

/**
 * What a start step's storage failure left behind: `refuse` rethrows, `tolerated`
 * proceeds durably-intended, `memory-only` proceeds with the fence refusing
 * ledger writes for the rest of the consult.
 */
type StartStorageOutcome = 'refuse' | 'tolerated' | 'memory-only';

/**
 * Ledger only: the mirrors carry row, chunk and completion state, so a start
 * survives a storage failure (memory-only — a reload still loses it). Legacy
 * has no memory path. A closed fence is logout, never tolerated.
 *
 * Tolerance and arming are separate decisions. Tolerance is broad, because
 * refusing a clinician a recording over a glitch is the worse outcome; arming is
 * narrow, because it latches the whole consult to memory. A tolerated transient
 * therefore proceeds against the healthy store: `createRecording` has already
 * mirrored the row unpersisted, the next drain's heal writes it, and a store that
 * is genuinely dying re-reports at the first chunk write, which arms there.
 */
const tolerateLedgerStorageFailure = (
  step: 'create' | 'canary' | 'low-storage',
  err: unknown,
  report: StorageLessReport
): StartStorageOutcome => {
  if (getPipelineMode() !== 'ledger' || isWriteFenceStaleError(err)) return 'refuse';
  if (!isStorageDegradation(err)) return 'refuse';
  // The fence step already reported this recording as storage-less; every
  // later refusal is that same event repeated once per write.
  if (isMemoryOnlyWriteError(err)) return 'memory-only';
  if (!isStoreUnusableForConsult(err)) {
    // Not the storage-less event: this recording still has its storage. Logged
    // all the same, because its row is memory-backed until the heal lands.
    logger.warn({
      event_type: 'LEDGER_START_WRITE_TOLERATED',
      details: {
        step,
        ...getSafeErrorFields(err),
        errorName: getUnwrappedErrorName(err),
      },
    });
    return 'tolerated';
  }
  // Tolerating alone refuses nothing, so every chunk that follows would spend a
  // four-attempt retry ladder against the store that just failed.
  const armed = armMemoryOnlyWrites();
  report({
    step,
    memoryOnly: armed.memoryOnly,
    v2Failed: armed.failed.v2,
    legacyFailed: armed.failed.legacy,
    ...getSafeErrorFields(err),
    // Dexie reports the exception it raised, so an exhausted quota surfaces as
    // `AbortError` — unwrap it or triage chases the wrong failure.
    errorName: getUnwrappedErrorName(err),
  });
  return 'memory-only';
};

// Ledger-only: lets the host pin an upload transport on the fresh recording
// row before capture starts (so the first chunk already routes through it).
// Best-effort — any failure leaves the recording on the HTTP path.
const prepareUploadTransport = async (
  config: SessionRecordingConfig | null,
  sessionId: string,
  recordingId: string
) => {
  if (getPipelineMode() !== 'ledger') return;
  try {
    await config?.prepareUploadTransport?.(sessionId, recordingId);
  } catch (err) {
    logger.warn({
      event_type: 'PREPARE_UPLOAD_TRANSPORT_FAILED',
      recording_id: recordingId,
      // Message only — a raw error can carry a request payload into the log.
      details: { error: err instanceof Error ? err.message : String(err) },
    });
  }
};

// The legacy start() brackets a 5MB `initialise` canary with pre/post
// deletes. The v2 canary (64KB, fixed sentinel key) self-cleans, so the
// bracketing deletes are no-ops in v2 mode.
const deleteStartCanary = async () => {
  if (getPipelineMode() === 'ledger') return;
  await db.deleteInitialise();
};

const createStartCanary = async () => {
  if (getPipelineMode() === 'ledger') {
    void requestPersistentStorage(); // Once per page load.
    await runCanary();
    return;
  }
  await db.createInitialise();
};

const persistRecordingEndDate = (sessionId: string, recordingId?: string | null) =>
  getPipelineMode() === 'ledger'
    ? setRecordingEndDateV2(sessionId, recordingId ?? undefined)
    : db.setRecordingEndDate(sessionId);

export const RECORDER_TAIL_LOCK_TIMEOUT_MS = 5000;

const waitForRecorderTail = (tail: Promise<unknown>[]): Promise<boolean> =>
  waitForSettledAudioWork(tail, RECORDER_TAIL_LOCK_TIMEOUT_MS);

// A same-tab 'recording' hold is transient — blaming "another tab" sends the
// user hunting for a tab that does not exist.
const adoptOrRefuseCaptureLock = (): CaptureLockToken => {
  if (!hasLocalCaptureLease('recording')) {
    throw new RecordingActiveInAnotherTabError('recording active in another tab');
  }
  // Take the finishing recording's tail lease over rather than make the user
  // wait out its drain; this recording's own hold guards the same uploads.
  const adopted = adoptCaptureLock();
  if (!adopted) {
    throw new StartRecordingFailed(
      'start blocked: previous recording still finishing in this tab'
    );
  }
  logger.info({ event_type: 'CAPTURE_LOCK_ADOPTED', details: { purpose: 'recording' } });
  return adopted;
};

export interface RecordingSlice {
  sessionId?: string | null;
  config: SessionRecordingConfig | null;
  status: Status;
  recordingId: string | null;
  // The live capture owns this key until teardown; navigation only changes sessionId.
  activeRecordingKey: Readonly<RecordingKey> | null;
  recorderStreamId: string | null;
  // When start() last succeeded (ms). Null once a termination path has consumed
  // it, which is what limits early-end diagnostics to one log per recording.
  recordingStartedAt: number | null;
  backupHydrationDone: boolean;
  recordersInProgress: number;
  start: (isTabSharingSession: boolean) => Promise<string>;
  stop: () => Promise<void>;
  handleStopError: () => Promise<void>;
  teardownForLogout: (reason?: 'logout' | 'remote-shutdown') => Promise<void>;
  setSessionId: (sessionId: string) => void;
  setConfig: (config: SessionRecordingConfig) => void;
  cleanupRecordings: () => Promise<void>;
}

// A recording that ends this quickly is almost always a denied permission,
// device loss or double-click stop, so terminations inside the window log
// extra diagnostic context.
const EARLY_END_THRESHOLD_MS = 5000;

type EarlyEndReason = 'stop' | 'stop_error' | 'interruption';

// Coarse buckets for dashboard grouping; `durationMs` is logged too. The
// boundaries separate the likely causes: race or permission, device loss,
// double-click stop.
type DurationBucket =
  | 'lt_500ms'
  | '500ms_to_1500ms'
  | '1500ms_to_3000ms'
  | '3000ms_to_5000ms';

const bucketDuration = (ms: number): DurationBucket => {
  if (ms < 500) return 'lt_500ms';
  if (ms < 1500) return '500ms_to_1500ms';
  if (ms < 3000) return '1500ms_to_3000ms';
  return '3000ms_to_5000ms';
};

export const createRecordingSlice: StateCreator<
  SessionRecordingStore,
  [],
  [],
  RecordingSlice
> = (set, get) => {
  const activeStartControllers = new Set<AbortController>();
  let logoutTeardownStarted = false;
  let inFlightStart: Promise<string> | null = null;
  let activeRecordingLockToken: CaptureLockToken | null = null;

  const releaseRecordingLock = (token = activeRecordingLockToken): void => {
    if (!token) return;
    releaseCaptureLock(token);
    if (activeRecordingLockToken === token) activeRecordingLockToken = null;
  };

  // The capture lock rides the in-flight upload tail (bounded) so a hydrating
  // tab cannot re-dispatch this recording's still-`pending` uploads. Riding the
  // tail is also what makes the lease adoptable — nothing else may be swapped.
  const releaseRecordingLockAfterUploads = (
    recordingId: string | null,
    token = activeRecordingLockToken
  ): void => {
    if (!token) return;
    markAdoptableTail(token);
    // The lock guards the tail CHUNK against a sibling tab's recovery re-send;
    // the backup has its own row and recency guard, so a slow backup upload must
    // not hold the recording lock.
    const tailExclude = ['backup-upload'] as const;
    void drainAudioWork(RECORDER_TAIL_LOCK_TIMEOUT_MS, { exclude: tailExclude }).then(
      (uploadsSettled) => {
        if (!uploadsSettled) {
          logger.warn({
            event_type: 'RECORDER_TAIL_LOCK_TIMEOUT',
            recording_id: recordingId ?? undefined,
            details: {
              timeoutMs: RECORDER_TAIL_LOCK_TIMEOUT_MS,
              pending: getPendingAudioWorkKinds(tailExclude),
            },
          });
        }
        releaseRecordingLock(token);
      }
    );
  };

  const assertStartActive = (signal: AbortSignal): void => {
    if (!signal.aborted && !logoutTeardownStarted) return;
    if (signal.reason instanceof StartRecordingCancelled) throw signal.reason;
    throw new StartRecordingCancelled('recording start cancelled by logout');
  };

  // Logs one snapshot per recording when it ends inside EARLY_END_THRESHOLD_MS.
  // Consumes `recordingStartedAt`, so of the termination paths that can run back
  // to back only the first reports.
  const logEarlyEndIfApplicable = (
    reason: EarlyEndReason,
    recordingKey: Readonly<RecordingKey> | null
  ): void => {
    const startedAt = get().recordingStartedAt;
    if (startedAt == null) return;

    // Consume the window unconditionally so a > 5s recording's termination
    // path doesn't leave a stale timestamp for the next recording.
    set({ recordingStartedAt: null });

    const durationMs = Date.now() - startedAt;
    if (durationMs >= EARLY_END_THRESHOLD_MS) return;

    // Defensive reads: the store is composed at runtime, and this already runs
    // on an error path — a missing slice must not throw.
    const audioGraph = useAudioGraphStore.getState();
    const recordingState = get();

    // Group-by dimensions are promoted to the top level, and the errors array is
    // mirrored as a csv and a boolean so text and boolean filters work on it.
    const audioGraphErrorsCsv = audioGraph.errors.join(',') || 'none';
    const audioGraphHadError = audioGraph.errors.length > 0;
    logger.warn({
      event_type: 'RECORDING_ENDED_EARLY',
      recording_id: recordingKey?.recordingId,
      reason,
      durationMs,
      durationBucket: bucketDuration(durationMs),
      audioGraphErrorsCsv,
      audioGraphHadError,
      details: {
        sessionId: recordingKey?.sessionId,
        recorderStreamId: recordingState.recorderStreamId,
        chunkRecorderCount: Object.keys(recordingState.chunkRecorders ?? {}).length,
        captureRecorderCount: recordingState.captureSlot ? 1 : 0,
        backupRecorderCount: Object.keys(recordingState.backupRecorders ?? {}).length,
        backupCaptureRecorderCount: recordingState.backupCaptureSlot ? 1 : 0,
        processingChunkCount: recordingState.processingChunksIds?.length ?? 0,
        processingTask: recordingState.processingTask ?? null,
        taskQueueTypes: (recordingState.taskQueue ?? []).map((t) => t.type),
        isPaused: recordingState.isPaused ?? false,
        audioGraph: {
          isReady: audioGraph.isReady,
          errors: audioGraph.errors,
          errorsCsv: audioGraphErrorsCsv,
          hadError: audioGraphHadError,
          // A short stable hash — correlates "same device" across a
          // user's logs without shipping the label (often a personal name).
          deviceLabelHash: hashDeviceLabel(audioGraph.device?.label),
          deviceId: audioGraph.device?.deviceId ?? null,
          availableDeviceCount: audioGraph.availableDevices?.length ?? 0,
        },
      },
    });
  };

  // Each start-cleanup step logs and continues: one failing step must not stop
  // the rest, and a rejected stamp must not surface as an unhandled rejection.
  // A failed discard or release leaves capture live under a READY store, so
  // those steps log at error; a failed stamp only costs a later cleanup pass.
  const attemptStartCleanup = (
    step: string,
    recordingId: string | undefined,
    fn: () => unknown,
    level: 'error' | 'warn' = 'warn'
  ): void => {
    const onError = (err: unknown) =>
      logger[level]({
        event_type: 'START_ROLLBACK_STEP_FAILED',
        recording_id: recordingId,
        details: {
          step,
          ...getSafeErrorFields(err),
          errorName: getUnwrappedErrorName(err),
        },
      });
    try {
      const result = fn() as { catch?: (onRejected: typeof onError) => unknown };
      if (typeof result?.catch === 'function') result.catch(onError);
    } catch (err) {
      onError(err);
    }
  };

  // A start that fails past RECORDING must not leave the store claiming a live
  // recording: the re-entry guard would hand the next Start a stale id with no
  // recorder behind it, so the consult would record nothing. Storage stamps are
  // best-effort and detached — the state reset is what the next start needs.
  const rollbackStartedRecording = (
    recordingKey: Readonly<RecordingKey>,
    recordingRowWritten: boolean
  ): void => {
    const { sessionId, recordingId } = recordingKey;
    const attempt = (step: string, fn: () => unknown, level?: 'error' | 'warn') =>
      attemptStartCleanup(step, recordingId, fn, level);
    // Only this start's recorders: stopped legacy recorders stay in their maps
    // until their uploads finish. Nothing captured in the window a failed start
    // leaves is worth a chunk, and a stop() would complete a recording the host
    // is about to report as failed.
    attempt(
      'discardChunkRecorder',
      () => get().discardChunkRecorder(recordingId),
      'error'
    );
    attempt(
      'discardBackupRecorder',
      () => get().discardBackupRecorder(recordingId),
      'error'
    );
    const { recorderStreamId } = get();
    if (recorderStreamId) {
      attempt(
        'releaseStream',
        () => useAudioGraphStore.getState().releaseStream(recorderStreamId),
        'error'
      );
    }
    // Ledger: a refused create still mirrors the row, and the drain would heal
    // that mirror into a live-looking recording, so the mirror decides.
    const rowKnown =
      getPipelineMode() === 'ledger'
        ? peekRecordingRow(recordingKey) !== undefined
        : recordingRowWritten;
    if (rowKnown) {
      // Ledger: endDate plus lastChunkIndex -1 make this an empty stopped row,
      // so hydrate's crash pass skips it. Legacy only records the end.
      attempt('setRecordingEndDate', () =>
        persistRecordingEndDate(sessionId, recordingId)
      );
      if (getPipelineMode() === 'ledger') {
        attempt('setLastChunkIndex', () => setLastChunkIndex(recordingKey, -1));
      }
    }
    set({
      status: 'READY',
      recordingId: null,
      recorderStreamId: null,
      activeRecordingKey: null,
    });
    resetStorageDegradedReport();
    logger.warn({
      event_type: 'START_ROLLED_BACK',
      recording_id: recordingId,
      details: { stampAttempted: rowKnown },
    });
  };

  const slice: RecordingSlice = {
    sessionId: null,
    config: null,
    status: 'READY',
    recordingId: null,
    activeRecordingKey: null,
    recorderStreamId: null,
    recordingStartedAt: null,
    backupHydrationDone: false,
    recordersInProgress: 0,

    setSessionId: (sessionId: string) => {
      logoutTeardownStarted = false;
      set({ sessionId, isPaused: false });
    },
    setConfig: (config: SessionRecordingConfig) => set({ config }),

    start: async (isTabSharingSession: boolean) => {
      if (logoutTeardownStarted) {
        throw new StartRecordingCancelled('recording start cancelled by logout');
      }
      // Checkpoints measure phase durations. The run is opened by handlePlay
      // and closed by finishTracking in useRecordingV2, so phases are measured
      // from the accepted start action.

      // Re-entry guard: a second start would tear down the live recorders and
      // POST a second recording id, orphaning a `live_transcript` row. Return the
      // existing id so the caller's downstream logic stays consistent.
      if (get().status === 'RECORDING') {
        const existingRecordingKey = get().activeRecordingKey;
        const existingRecordingId = existingRecordingKey?.recordingId;
        logger.warn({
          event_type: 'START_REENTRY_BLOCKED',
          details: {
            currentRecordingId: existingRecordingId,
            sessionId: existingRecordingKey?.sessionId,
          },
        });
        if (!existingRecordingId) {
          const error = new StartRecordingFailed('active recording identity missing');
          capturePerf.finishStartRecordingTrackingFailure(error);
          throw error;
        }
        // The live capture already crossed the start-success boundary. Any
        // current START entry belongs to this re-entry press, so terminalize it
        // as cancelled before the caller can misreport a second success.
        capturePerf.finishStartRecordingTrackingFailure(
          new StartRecordingCancelled('recording is already active')
        );
        return existingRecordingId;
      }

      // A start that is still in async prep has not reached RECORDING yet, so
      // the guard above cannot see it. Share its outcome rather than opening a
      // second run that would tear down the first one's recorders and lease.
      if (inFlightStart) {
        logger.warn({
          event_type: 'START_REENTRY_BLOCKED',
          details: {
            phase: 'in_flight',
            sessionId: get().sessionId,
          },
        });
        return inFlightStart;
      }

      // Snapshot before the first await: routing can change during any prep step.
      const sessionId = get().sessionId;

      // A new recording gets its own storage warning. The teardown paths clear
      // this too; doing it here as well means a missed teardown costs the next
      // recording a warning it needed, rather than silencing it for good.
      resetStorageDegradedReport();

      // Pin the persistence pipeline before any storage call so one recording
      // can never straddle the legacy and v2 stores (no-op once pinned).
      const pipelineMode = await resolvePipelineMode();

      const useTranscriptionServiceV2 = captureFlags.useTranscriptionServiceV2();
      const checkpoint = (name: string) =>
        capturePerf.appendStartRecordingCheckpoint({
          checkpointName: name,
        });

      const startController = new AbortController();
      const { signal } = startController;
      activeStartControllers.add(startController);
      let startCaptureLockToken: CaptureLockToken | null = null;
      let startAdoptedCaptureLock = false;
      // Set once the store reads RECORDING, so the catch knows to roll back.
      let startedRecordingKey: Readonly<RecordingKey> | null = null;
      let recordingRowWritten = false;
      // One event per start, first reporter wins: the fence step runs first and
      // already names both populations, so a tolerated step after it is the
      // same start reported twice.
      let storageLessReported = false;
      const reportStorageLess: StorageLessReport = (details) => {
        if (storageLessReported) return;
        storageLessReported = true;
        logger.warn({ event_type: 'LEDGER_STARTED_WITHOUT_STORAGE', details });
      };
      // The warning says a recording is live but not durable, so it must not
      // fire before the gates that can still throw. Staged here, released once
      // the recording has an id.
      let pastStartGates = false;
      let stagedStorageDegraded: { error: unknown } | null = null;
      const reportStorageDegraded: StorageDegradedReport = (error) => {
        if (!pastStartGates) {
          stagedStorageDegraded ??= { error };
          return;
        }
        // Once per RECORDING, through the latch the mid-recording arm shares.
        reportStorageDegradedOnce(get, get().recordingId, error);
      };
      const releaseStagedStorageDegraded = () => {
        pastStartGates = true;
        const staged = stagedStorageDegraded;
        stagedStorageDegraded = null;
        if (staged) reportStorageDegraded(staged.error);
      };
      try {
        if (typeof sessionId !== 'string' || !sessionId.trim()) {
          throw new StartRecordingFailed('session ID missing');
        }
        // The only point an armed suppression lifts: nothing recording, nothing
        // in flight, so a healed store gets reclassified. Gated on the capture
        // lock, which spans the capture lifetime — `status` does not. Drainable,
        // not retained: exhausted bytes wait for the logout purge, never a drain.
        if (
          !hasAnyLocalCaptureLease() &&
          getPendingAudioWorkCount() === 0 &&
          !hasDrainableRetainedChunks() &&
          !isRetryDrainActive()
        ) {
          disarmUnusableStoreWrites();
        }
        const fenceOutcome = await prepareWriteFenceForHydrate();
        assertStartActive(signal);
        if (fenceOutcome === 'store-unusable' && pipelineMode === 'ledger') {
          // Armed here, not in the classifier, so the suppression is scoped to a
          // load that actually records. A legacy-only failure keeps durable
          // ledger writes; a ledger failure goes memory-only.
          const armed = armUnusableStoreWrites();
          // Once per armed start, both populations: `memoryOnly` says whether
          // this recording lost its storage, and a legacy-only failure is still
          // a wipe that could not finish.
          if (armed) {
            reportStorageLess({
              step: 'fence',
              memoryOnly: armed.memoryOnly,
              v2Failed: armed.failed.v2,
              legacyFailed: armed.failed.legacy,
            });
            // A legacy-only failure keeps durable ledger writes, so this
            // recording lost nothing and the clinician needs no warning.
            if (armed.memoryOnly) reportStorageDegraded(storageDegradedError('fence'));
          }
        } else if (fenceOutcome !== 'ready') {
          logger.error({ event_type: 'START_WRITE_FENCE_RECOVERY_FAILED' });
          throw new AudioStorageRecoveryFailed(
            'could not safely recover local audio storage'
          );
        }
        resumeAudioWork();

        if (pipelineMode === 'ledger') {
          const captureLock = await tryAcquireCaptureLock('recording');
          const adopting = captureLock.status === 'unavailable';
          const captureLockToken = adopting
            ? adoptOrRefuseCaptureLock()
            : captureLock.token;
          startCaptureLockToken = captureLockToken;
          startAdoptedCaptureLock = adopting;
          activeRecordingLockToken = captureLockToken;
        }

        const {
          start: startAudioSystem,
          getRecorderStream,
          clearError,
          createTabSource,
        } = useAudioGraphStore.getState();

        // `start_invoked` measures the click → start() entry gap (debounce,
        // React renders, task-queue wait).
        checkpoint('start_invoked');

        const userRefId = await captureUserContext.resolveCurrentUserRefId();
        assertStartActive(signal);
        if (!userRefId) {
          logger.error({ event_type: 'RECORDING_USER_REF_ID_MISSING' });
          throw new StartRecordingFailed('could not resolve user ref id');
        }

        if (isTabSharingSession) {
          await createTabSource(signal);
          assertStartActive(signal);
          checkpoint('tab_source_created');
        }

        logger.info({ event_type: 'START_TRIGGERED' });

        // The four prep steps have no data dependencies, so they run
        // concurrently. Trade-off: mic readiness is checked after Promise.all, so
        // a failed mic still spends a round trip on an orphaned recording id.

        // Canary cleanup must never block a new recording; its own checkpoint
        // keeps a swallowed failure distinguishable from a clean one.
        const idbP = deleteStartCanary()
          .then(() => {
            assertStartActive(signal);
            checkpoint('delete_initialise_pre');
          })
          .catch((err) => {
            logger.warn({
              event_type: 'DELETE_INITIALISE_PRE_FAILED',
              details: { error: err instanceof Error ? err.message : String(err) },
            });
            checkpoint('delete_initialise_pre_failed');
          });
        // The ledger records from memory, so an exhausted quota must not abort
        // the start. Its own checkpoint keeps a memory-only start from reading
        // as a healthy one.
        const storageP = checkStorageAvailable().then(
          () => {
            assertStartActive(signal);
            checkpoint('storage_checked');
          },
          (err) => {
            const outcome = tolerateLedgerStorageFailure(
              'low-storage',
              err,
              reportStorageLess
            );
            if (outcome === 'refuse') throw err;
            assertStartActive(signal);
            // Legacy throws this to the host; the ledger absorbs it, so the
            // warning crosses as a port instead. Only an armed start warns: a
            // tolerated transient still records to disk.
            if (outcome === 'memory-only') reportStorageDegraded(err);
            checkpoint('storage_checked_failed');
          }
        );
        // Clear INTERRUPTED inside audioP's .then so it fires the moment audio
        // resolves. Otherwise a stale INTERRUPTED error would remain visible
        // to UI subscribers for the duration of the network round-trip.
        const audioP = startAudioSystem(signal).then(() => {
          assertStartActive(signal);
          clearError('INTERRUPTED');
          checkpoint('audio_system_started');
        });
        const networkP = tryToGetRecordingId(
          get().config!,
          sessionId,
          useTranscriptionServiceV2
        ).then((id) => {
          assertStartActive(signal);
          checkpoint('recording_id_fetched');
          return id;
        });
        // Promise.all rejects on the first failure, so the siblings need an
        // observer or they surface as unhandled rejections. idbP already resolves
        // through its own .catch.
        storageP.catch(() => undefined);
        audioP.catch(() => undefined);
        networkP.catch(() => undefined);
        const [, , , recordingId] = await Promise.all([idbP, storageP, audioP, networkP]);
        assertStartActive(signal);
        if (typeof recordingId !== 'string' || !recordingId.trim()) {
          throw new CreateRecordingIdFailed('could not get recording ID');
        }
        const recordingKey = Object.freeze({ sessionId, recordingId });

        const { isReady, errors } = useAudioGraphStore.getState();
        if (!isReady || errors.length) {
          // The recording-id was fetched in parallel with audio init, so a
          // failed mic here leaves an orphaned record on the t11n side.
          // Logging so server-side ops can detect and reconcile.
          logger.warn({
            event_type: 'ORPHANED_RECORDING_ID',
            details: { recordingId },
          });
          logger.error({
            event_type: 'START_FAILED',
            details: { isReady, errors },
          });
          throw new StartRecordingFailed('could not start recording');
        }
        // Only emit parallel_prep_complete on the success path so Datadog
        // cohort comparisons aren't polluted by failed-mic traces that
        // appear to succeed.
        checkpoint('parallel_prep_complete');

        assertStartActive(signal);
        const { stream, id: recorderStreamId } = getRecorderStream(() => {
          logger.error({
            event_type: 'INTERRUPTION',
            recording_id: recordingKey.recordingId,
          });
          // A stream that dies in the first seconds is almost always a device
          // loss or a revoked permission, and this snapshot is the only place
          // that context survives.
          logEarlyEndIfApplicable('interruption', recordingKey);
          get().config!.onInterrupt?.();
        });

        set({
          status: 'RECORDING',
          recordingId,
          recorderStreamId,
          activeRecordingKey: recordingKey,
        });
        startedRecordingKey = recordingKey;
        // Past the gates that throw, and the recording now has an id to latch
        // on: anything the fence or low-storage steps staged fires here, ahead
        // of create/canary so the earliest cause is the one reported.
        releaseStagedStorageDegraded();
        try {
          await persistCreateRecording(
            sessionId,
            recordingId,
            useTranscriptionServiceV2,
            userRefId
          );
        } catch (err) {
          assertStartActive(signal);
          const outcome = tolerateLedgerStorageFailure('create', err, reportStorageLess);
          if (outcome === 'refuse') throw err;
          if (outcome === 'memory-only')
            reportStorageDegraded(storageDegradedError('create'));
        }
        recordingRowWritten = true;
        assertStartActive(signal);
        await prepareUploadTransport(get().config, sessionId, recordingId);
        assertStartActive(signal);
        checkpoint('recording_created');

        try {
          await createStartCanary();
        } catch (err) {
          assertStartActive(signal);
          const outcome = tolerateLedgerStorageFailure('canary', err, reportStorageLess);
          if (outcome === 'refuse') {
            throw new DexieStartError(
              getPipelineMode() === 'ledger'
                ? 'Failed to write audio ledger canary'
                : 'Failed to write dexie initialise chunk',
              { cause: err }
            );
          }
          if (outcome === 'memory-only')
            reportStorageDegraded(storageDegradedError('canary'));
        }
        assertStartActive(signal);
        checkpoint('initialise_created');

        // Use the parent's identity, not route-backed state that may have changed during prep.
        get().startChunkRecorder(stream, useTranscriptionServiceV2, recordingKey);
        get().startBackupRecorder(stream, recordingKey);
        checkpoint('recorders_started');
        // Recording is already live here, so a canary-delete failure must not
        // reach the outer catch — that resets status to READY and stops the
        // recording a beat after it appears to start.
        try {
          await deleteStartCanary();
          assertStartActive(signal);
          checkpoint('delete_initialise_post');
        } catch (err) {
          // `_failed` keeps the swallowed-cleanup path distinguishable from
          // success in Datadog cohorting (see pre-start equivalent above).
          logger.warn({
            event_type: 'DELETE_INITIALISE_POST_FAILED',
            details: { error: err instanceof Error ? err.message : String(err) },
          });
          checkpoint('delete_initialise_post_failed');
        }
        assertStartActive(signal);
        // Anchored where recording is actually live, not at start() entry, so
        // prep latency does not count against the early-end window.
        set({ recordingStartedAt: Date.now() });
        logger.info({
          event_type: 'START_SUCCESS',
          recording_id: recordingId,
        });
        return recordingId;
      } catch (err) {
        // An adopted lease still guards the superseded recording's pending
        // uploads, so dropping it here would reopen the cross-tab re-dispatch
        // window; it has to ride the same bounded drain a stop would give it.
        if (startAdoptedCaptureLock) {
          releaseRecordingLockAfterUploads(
            get().recordingId ?? null,
            startCaptureLockToken
          );
        } else {
          releaseRecordingLock(startCaptureLockToken);
        }
        if (signal.aborted || logoutTeardownStarted) {
          logger.info({ event_type: 'START_CANCELLED_FOR_LOGOUT' });
          const cancellation =
            signal.reason instanceof StartRecordingCancelled
              ? signal.reason
              : new StartRecordingCancelled('recording start cancelled by logout');
          capturePerf.finishStartRecordingTrackingFailure(cancellation);
          throw cancellation;
        }
        if (startedRecordingKey) {
          rollbackStartedRecording(startedRecordingKey, recordingRowWritten);
        }
        // Nothing else reliably releases display capture on a failed start; a
        // leaked source stays wired into the next consult. `false`: the bare
        // disconnect() drops every edge, and a targeted one throws if the
        // analyzer isn't wired.
        if (isTabSharingSession) {
          attemptStartCleanup('cleanupTabSource', startedRecordingKey?.recordingId, () =>
            useAudioGraphStore.getState().cleanupTabSource(false)
          );
        }
        capturePerf.finishStartRecordingTrackingFailure(err);
        logger.error({
          event_type: 'START_ERROR',
          details: await getErrorInfo(err),
        });
        get().config!.onError?.(err);
        throw err;
      } finally {
        activeStartControllers.delete(startController);
      }
    },

    teardownForLogout: async (reason = 'logout') => {
      logoutTeardownStarted = true;
      // Remote shutdown: the sibling tab's fence is already closed, so a final
      // chunk could only fail — discard in both modes. Local logout: the ledger
      // latches at once, legacy gets a bounded persist+upload window first.
      const preserveLegacyTail = reason === 'logout' && getPipelineMode() !== 'ledger';
      if (!preserveLegacyTail) beginAudioShutdown();
      const recorderTails: Promise<unknown>[] = [];
      const cancellation = new StartRecordingCancelled(
        'recording start cancelled by logout'
      );
      activeStartControllers.forEach((controller) => controller.abort(cancellation));

      const queuedTasks = get().taskQueue ?? [];
      set({ taskQueue: [], isPaused: true });
      queuedTasks.forEach((task) => task.reject(cancellation));

      const audioGraph = useAudioGraphStore.getState();
      Object.values(get().chunkRecorders).forEach((chunkRecorder) => {
        try {
          audioGraph.releaseMicAnalyzer(chunkRecorder.micAnalyzerId);
          if (chunkRecorder.tabAnalyzerId) {
            audioGraph.releaseTabAnalyzer(chunkRecorder.tabAnalyzerId);
          }
        } catch (err) {
          logger.warn({
            event_type: 'LOGOUT_RELEASE_ANALYZER_FAILED',
            details: { error: err instanceof Error ? err.message : String(err) },
          });
        }
        try {
          if (preserveLegacyTail) recorderTails.push(chunkRecorder.stop());
          else chunkRecorder.discard();
        } catch (err) {
          logger.warn({
            event_type: 'LOGOUT_STOP_CHUNK_RECORDER_FAILED',
            details: { error: err instanceof Error ? err.message : String(err) },
          });
        }
      });
      Object.values(get().backupRecorders).forEach((backupRecorder) => {
        try {
          if (preserveLegacyTail) recorderTails.push(backupRecorder.stop());
          else backupRecorder.discard();
        } catch (err) {
          logger.warn({
            event_type: 'LOGOUT_STOP_BACKUP_RECORDER_FAILED',
            details: { error: err instanceof Error ? err.message : String(err) },
          });
        }
      });
      // Ledger capture slots always discard at teardown: nothing may upload
      // after the user asks to leave, and remote shutdown's fence is closed.
      try {
        discardCaptureSlotV2(set, get);
      } catch (err) {
        logger.warn({
          event_type: 'LOGOUT_STOP_CHUNK_RECORDER_FAILED',
          details: { error: err instanceof Error ? err.message : String(err) },
        });
      }
      try {
        discardBackupCaptureSlotV2(set, get);
      } catch (err) {
        logger.warn({
          event_type: 'LOGOUT_STOP_BACKUP_RECORDER_FAILED',
          details: { error: err instanceof Error ? err.message : String(err) },
        });
      }

      const recorderStreamId = get().recorderStreamId;
      if (recorderStreamId) {
        try {
          audioGraph.releaseStream(recorderStreamId);
        } catch (err) {
          logger.warn({
            event_type: 'LOGOUT_RELEASE_STREAM_FAILED',
            details: { error: err instanceof Error ? err.message : String(err) },
          });
        }
      }
      try {
        audioGraph.teardownForLogout();
      } catch (err) {
        logger.warn({
          event_type: 'LOGOUT_AUDIO_GRAPH_TEARDOWN_FAILED',
          details: { error: err instanceof Error ? err.message : String(err) },
        });
      }

      set({
        status: 'READY',
        recordingId: null,
        activeRecordingKey: null,
        recorderStreamId: null,
        recordingStartedAt: null,
        chunkRecorders: {},
        captureSlot: null,
        backupRecorders: {},
        backupCaptureSlot: null,
        processingChunksIds: [],
        recordersInProgress: 0,
        processingTask: null,
      });
      resetStorageDegradedReport();
      releaseRecordingLock();
      // The wipe controller awaits this before closing the fence, so the
      // legacy tail's persist and upload run against open storage.
      if (recorderTails.length > 0 && !(await waitForRecorderTail(recorderTails))) {
        logger.warn({
          event_type: 'LOGOUT_RECORDER_TAIL_TIMEOUT',
          details: { timeoutMs: RECORDER_TAIL_LOCK_TIMEOUT_MS },
        });
      }
    },

    stop: async () => {
      const recordingKey = get().activeRecordingKey;
      // Remote fence shutdown tears down capture before invoking the existing
      // interruption callback, whose first action is a defensive stop().
      if (!recordingKey) {
        logger.warn({
          event_type: 'STOP_SKIPPED_NO_ACTIVE_RECORDING',
          details: {
            hasSessionId: !!get().sessionId,
            hasRecordingId: !!get().recordingId,
          },
        });
        return;
      }
      const { sessionId, recordingId } = recordingKey;
      try {
        const { releaseStream, cleanupTabSource } = useAudioGraphStore.getState();
        logger.info({
          event_type: 'STOP_TRIGGERED',
          recording_id: recordingId,
        });
        // Before teardown: the snapshot must see the recorders, stream and task
        // queue as they were while recording was live.
        logEarlyEndIfApplicable('stop', recordingKey);
        await persistRecordingEndDate(sessionId, recordingId);
        const recorderTail = [
          get().stopChunkRecorder(recordingId),
          get().stopBackupRecorder(recordingId),
        ];
        set({ status: 'READY', recordingId: null, recordingStartedAt: null });
        // With the recording id, so the next one may warn about its own storage.
        resetStorageDegradedReport();
        const { recorderStreamId } = get();

        if (recorderStreamId) {
          releaseStream(recorderStreamId);
          set({ recorderStreamId: null });
        }
        cleanupTabSource();
        // Persist-bound: the tail settles when the final chunk/backup rows
        // are durably written; their uploads continue detached.
        if (!(await waitForRecorderTail(recorderTail))) {
          logger.warn({
            event_type: 'RECORDER_TAIL_PERSIST_TIMEOUT',
            recording_id: recordingId,
            details: { timeoutMs: RECORDER_TAIL_LOCK_TIMEOUT_MS },
          });
        }
        releaseRecordingLockAfterUploads(recordingId);
        pokeRetryDrain(set, get, 'recording-stop');
        set({ activeRecordingKey: null });

        // Info only under ledger: the rollout needs a clean-stop denominator,
        // and legacy's log volume should not rise permanently to supply it.
        const stopSuccess = { event_type: 'STOP_SUCCESS', recording_id: recordingId };
        if (getPipelineMode() === 'ledger') logger.info(stopSuccess);
        else logger.debug(stopSuccess);
      } catch (err) {
        logger.error({
          event_type: 'STOP_ERROR',
          recording_id: recordingId,
          details: getSafeErrorFields(err),
        });
        // `handleStopError` below also reports the early end; the consumed
        // timestamp is what keeps it to one event.
        get().config!.onError?.(err);
        get().config!.onStopError?.();
        await get().handleStopError();
        throw err;
      }
    },

    handleStopError: async () => {
      const recordingKey = get().activeRecordingKey;
      const recordingId = recordingKey?.recordingId;
      logger.debug({
        event_type: 'HANDLE_STOP_ERROR_TRIGGERED',
        recording_id: recordingId,
      });
      // No-op if `stop()` already consumed the window, otherwise reports the
      // failed-stop variant (e.g. handleStopError invoked directly, or stop()
      // threw before the early-end check).
      logEarlyEndIfApplicable('stop_error', recordingKey);
      const tryAndContinue = async (
        func: () => void | Promise<void>,
        funcName: string
      ) => {
        try {
          await func();
        } catch (err) {
          logger.error({
            event_type: 'HANDLE_STOP_ERROR_ERROR',
            details: { funcName, errorInfo: await getErrorInfo(err) },
          });
        }
      };

      const stopAllChunkRecorders = async () => {
        const { releaseMicAnalyzer, releaseTabAnalyzer } = useAudioGraphStore.getState();
        const tail = Object.values(get().chunkRecorders).map((chunkRecorder) => {
          releaseMicAnalyzer(chunkRecorder.micAnalyzerId);
          if (chunkRecorder.tabAnalyzerId)
            releaseTabAnalyzer(chunkRecorder.tabAnalyzerId);
          return chunkRecorder.stop();
        });
        if (getPipelineMode() === 'ledger')
          tail.push(stopCaptureV2(set, get, recordingId));
        if (!(await waitForRecorderTail(tail))) {
          logger.warn({
            event_type: 'RECORDER_TAIL_PERSIST_TIMEOUT',
            recording_id: recordingId,
            details: { timeoutMs: RECORDER_TAIL_LOCK_TIMEOUT_MS },
          });
        }
      };

      const stopAllBackupRecorders = async () => {
        const tail = Object.values(get().backupRecorders).map((backupRecorder) =>
          backupRecorder.stop()
        );
        if (getPipelineMode() === 'ledger')
          tail.push(stopBackupCaptureV2(set, get, recordingId));
        if (!(await waitForRecorderTail(tail))) {
          logger.warn({
            event_type: 'RECORDER_TAIL_PERSIST_TIMEOUT',
            recording_id: recordingId,
            details: { timeoutMs: RECORDER_TAIL_LOCK_TIMEOUT_MS },
          });
        }
      };

      if (recordingKey) {
        await tryAndContinue(
          () => persistRecordingEndDate(recordingKey.sessionId, recordingKey.recordingId),
          'setRecordingEndDate'
        );
      }
      await tryAndContinue(() => stopAllChunkRecorders(), 'stopAllChunkRecorders');
      await tryAndContinue(() => stopAllBackupRecorders(), 'stopAllBackupRecorders');
      set({
        status: 'READY',
        recordingId: null,
        recordingStartedAt: null,
        activeRecordingKey: null,
      });
      resetStorageDegradedReport();
      const { recorderStreamId } = get();

      if (recorderStreamId) {
        await tryAndContinue(
          () => useAudioGraphStore.getState().releaseStream(recorderStreamId),
          'releaseStream'
        );
        set({ recorderStreamId: null });
      }
      releaseRecordingLockAfterUploads(recordingId ?? null);
      logger.debug({
        event_type: 'HANDLE_STOP_ERROR_FINISHED',
        recording_id: recordingId,
      });
    },

    // Every caller fires this task and walks away (`void cleanupRecordingsTask()`),
    // so a rejection here has nowhere to land but the host's unhandled-rejection
    // handler. Housekeeping is best effort — the next cleanup retries the rows.
    cleanupRecordings: async () => {
      // Outside the try: `cleanupRecordingsV2` already owns this contract, so
      // the catch below covers the legacy branch and nothing else.
      if (getPipelineMode() === 'ledger') return cleanupRecordingsV2(get);
      try {
        if (!get().config!.retainData) {
          await sleep(2500);
          await deleteFinishedRecordings(get().recordingId);
        }
      } catch (err) {
        logger.warn({
          event_type: 'CLEANUP_RECORDINGS_FAILED',
          details: getSafeErrorFields(err),
        });
      }
    },
  };

  return {
    ...slice,
    // Publishes the running start so the guards inside `slice.start` can hand
    // it to a concurrent caller. Wrapping the call is what makes the latch
    // total: it is cleared however the start settles, including the prep steps
    // that run before that function's own try/finally.
    start: (isTabSharingSession: boolean) => {
      if (inFlightStart) return slice.start(isTabSharingSession);

      const run = slice.start(isTabSharingSession);
      inFlightStart = run.finally(() => {
        inFlightStart = null;
      });
      // The latch is awaited only when a second caller actually arrives, so it
      // needs its own observer or a failed start reads as an unhandled
      // rejection. The run itself is still returned to, and handled by, the
      // caller that opened it.
      void inFlightStart.catch(() => undefined);
      return run;
    },
  };
};
